import { useEffect, useState } from "react";
import { Search, Plus, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { ProductFormDialog } from "../../components/shared/ProductFormDialog";
import { api } from "../../lib/api";

export default function CatalogPage() {
  const [catalog, setCatalog] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const load = () => api.get("/catalog/admin/products").then((r) => setCatalog(r.data)).catch(() => setCatalog([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingProduct(null); setFormOpen(true); };
  const openEdit = (p) => { setEditingProduct(p); setFormOpen(true); };

  const toggleFavorite = async (slug) => {
    try {
      const { data } = await api.post(`/catalog/admin/products/${slug}/favorite`);
      setCatalog((prev) => prev.map((p) => (p.slug === slug ? { ...p, is_favorite: data.is_favorite } : p)));
      toast.success(data.is_favorite ? "Ajouté aux coups de cœur de l'accueil" : "Retiré des coups de cœur");
    } catch {
      toast.error("Impossible de modifier le coup de cœur");
    }
  };
  return (
    <div data-testid="admin-catalog-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B04 — Catalogue &amp; Disponibilités</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Catalogue &amp; Disponibilités</h1>
          <p className="mt-1 text-sm text-muted-foreground">Disponibilité = possédé − engagé (période) − indisponibilités. Jamais de simple oui/non.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="admin-add-product">
          <Plus className="h-4 w-4" /> Ajouter un article
        </button>
      </div>

      <div className="card-ae mt-6">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
          <div className="relative flex-1 min-w-52">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input className="input-ae !pl-9" placeholder="Rechercher un article…" data-testid="admin-catalog-search" />
          </div>
          <select className="input-ae w-auto" data-testid="admin-catalog-category-filter" aria-label="Catégorie"><option>Toutes catégories</option></select>
          <select className="input-ae w-auto" data-testid="admin-catalog-status-filter" aria-label="Statut"><option>Statut : Actifs</option><option>Désactivés</option><option>Archivés</option></select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-catalog-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">Article</th>
                <th className="px-5 py-3">Référence</th>
                <th className="px-5 py-3">Catégorie</th>
                <th className="px-5 py-3 text-center">Possédé</th>
                <th className="px-5 py-3 text-center">Engagé (période)</th>
                <th className="px-5 py-3 text-center">Disponible</th>
                <th className="px-5 py-3 text-right">Prix TTC</th>
                <th className="px-5 py-3 text-center" title="Affiché dans « Nos coups de cœur » sur l'accueil (4 max)">Coup de cœur</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {catalog.map((p) => (
                <tr key={p.slug} className="transition-colors hover:bg-cream/60" data-testid={`admin-catalog-row-${p.slug}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover" />
                      <span className="font-semibold text-deepgreen">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">{p.slug}</td>
                  <td className="px-5 py-3 text-muted-foreground">{p.category}</td>
                  <td className="px-5 py-3 text-center">{p.owned_qty}</td>
                  <td className="px-5 py-3 text-center" title="Engagé calculé par période — voir Planning matériel">—</td>
                  <td className="px-5 py-3 text-center font-bold text-deepgreen">{p.owned_qty}</td>
                  <td className="px-5 py-3 text-right font-semibold">{p.price_ttc.toFixed(2)} €</td>
                  <td className="px-5 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!p.is_favorite}
                      onChange={() => toggleFavorite(p.slug)}
                      className="h-4 w-4 accent-[#2C4C3B]"
                      data-testid={`admin-favorite-toggle-${p.slug}`}
                      aria-label={`Coup de cœur — ${p.name}`}
                    />
                  </td>
                  <td className="px-5 py-3"><StatusBadge status={p.active ? "Actif" : "Archivé"} /></td>
                  <td className="px-5 py-3 text-right">
                    <button type="button" onClick={() => openEdit(p)} className="rounded-full p-1.5 text-deepgreen hover:bg-sage-light" data-testid={`admin-edit-product-${p.slug}`} aria-label={`Modifier ${p.name}`}>
                      <PencilLine className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
          Désactivation sans perte d'historique. Indisponibilités (casse, réparation, nettoyage, retour non confirmé) gérées par période dans le planning matériel.
        </p>
      </div>
      <ProductFormDialog open={formOpen} onOpenChange={setFormOpen} product={editingProduct} onSaved={load} />
    </div>
  );
}
